import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";

const ADVISORY = [
  "Ongoing virtual HR support", "HR process audits", "Employee onboarding and offboarding",
  "HR documentation and workflow development", "Employee handbook and policy drafting",
  "Employee engagement initiatives", "Performance management process support",
  "HR compliance guidance within professional scope", "HR systems and administrative support",
  "People operations consulting",
];
const RECRUITING = [
  "Recruitment strategy", "Job description development", "Job posting and sourcing",
  "Candidate screening", "Interview scheduling and coordination", "Hiring manager support",
  "Candidate experience improvements", "Recruitment process optimization", "Full-cycle recruiting projects",
];
const MODELS = [
  { title: "One-Time HR Projects", desc: "Scoped, deliverable-based engagements with a clear beginning and end." },
  { title: "Hourly HR Consulting", desc: "On-demand expertise billed by the hour for flexible support." },
  { title: "Monthly Fractional HR Retainers", desc: "An embedded people partner on a predictable monthly basis." },
  { title: "Recruiting Projects", desc: "Project-based or full-cycle recruiting support tailored to your hiring." },
];
const SERVICE_OPTIONS = [
  "Fractional HR & People Advisory", "HR Process Audit", "Onboarding / Offboarding",
  "Handbook & Policy", "Performance Management", "Recruiting & Talent Acquisition",
  "Employee Engagement", "People Operations",
];

export default function ForBusinesses() {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", email: "", phone: "", company_size: "",
    services: [], project_goals: "", budget: "", timeline: "", engagement_model: "", hp: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
      toast.success("Inquiry received. We'll be in touch shortly.");
      window.scrollTo({ top: document.getElementById("inquiry")?.offsetTop - 100, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-ivory border border-champagne/40 rounded-lg px-4 py-3 text-espresso focus:outline-none focus:ring-2 focus:ring-champagne focus:border-transparent placeholder:text-espresso/40";

  return (
    <div data-testid="for-businesses-page">
      <SEO title="For Businesses" description="Fractional HR, people advisory, and full-cycle recruiting — flexible, fully virtual support for growing businesses." />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-16">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">For Businesses</span>
          <h1 className="font-serif text-5xl md:text-7xl text-espresso mt-5 max-w-3xl leading-[1.02]">
            People strategy that grows with you.
          </h1>
          <p className="text-lg text-espresso/75 mt-6 max-w-2xl leading-relaxed">
            Intentional HR support and talent acquisition, delivered 100% virtually and scaled to your needs —
            from a single project to an ongoing fractional partnership.
          </p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 grid lg:grid-cols-2 gap-10 lg:gap-16">
        <Reveal>
          <div className="border-t border-espresso/15 pt-8">
            <h2 className="font-serif text-3xl md:text-4xl text-espresso">Fractional HR &amp; People Advisory</h2>
            <ul className="mt-6 space-y-3">
              {ADVISORY.map((x) => (
                <li key={x} className="flex gap-3 text-espresso/80"><Check size={18} className="text-champagne mt-1 shrink-0" />{x}</li>
              ))}
            </ul>
            <a href="#inquiry" data-testid="advisory-proposal" className="inline-block mt-8 bg-espresso text-ivory rounded-full px-7 py-3 font-medium hover:bg-forest transition-colors">Request a Proposal</a>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="border-t border-espresso/15 pt-8">
            <h2 className="font-serif text-3xl md:text-4xl text-espresso">Recruiting &amp; Talent Acquisition</h2>
            <ul className="mt-6 space-y-3">
              {RECRUITING.map((x) => (
                <li key={x} className="flex gap-3 text-espresso/80"><Check size={18} className="text-champagne mt-1 shrink-0" />{x}</li>
              ))}
            </ul>
            <a href="#inquiry" data-testid="recruiting-call" className="inline-block mt-8 border border-espresso text-espresso rounded-full px-7 py-3 font-medium hover:bg-espresso hover:text-ivory transition-colors">Book a Discovery Call</a>
          </div>
        </Reveal>
      </section>

      <section>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 border-t border-espresso/10">
          <Reveal><h2 className="font-serif text-4xl md:text-5xl text-espresso">Engagement models.</h2></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12 mt-12">
            {MODELS.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.08}>
                <div className="border-t border-espresso/15 pt-6 h-full">
                  <h3 className="font-serif text-xl text-espresso">{m.title}</h3>
                  <p className="text-espresso/70 mt-3 text-sm leading-relaxed">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section id="inquiry" className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32 scroll-mt-24">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-medium">Let's talk</span>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso mt-3">Request a proposal.</h2>
          <p className="text-espresso/70 mt-4">Tell us about your business and goals — we'll respond with next steps.</p>
        </Reveal>

        {done ? (
          <Reveal>
            <div data-testid="inquiry-success" className="mt-10 border border-champagne/40 rounded-2xl p-12 text-center">
              <Check className="mx-auto text-champagne" size={40} />
              <h3 className="font-serif text-3xl text-espresso mt-4">Thank you.</h3>
              <p className="text-espresso/70 mt-3">Your inquiry has been received. We'll be in touch shortly to schedule a discovery call.</p>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} data-testid="inquiry-form" className="mt-10 grid md:grid-cols-2 gap-5">
              <input type="text" value={form.hp} onChange={(e) => set("hp", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Company name</label>
                <input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} data-testid="inquiry-company" />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Contact name</label>
                <input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} data-testid="inquiry-contact" />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Email</label>
                <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="inquiry-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Phone (optional)</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="inquiry-phone" />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Company size</label>
                <select value={form.company_size} onChange={(e) => set("company_size", e.target.value)} className={inputCls} data-testid="inquiry-size">
                  <option value="">Select…</option>
                  <option>1–10</option><option>11–50</option><option>51–200</option><option>201–500</option><option>500+</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Engagement model</label>
                <select value={form.engagement_model} onChange={(e) => set("engagement_model", e.target.value)} className={inputCls} data-testid="inquiry-model">
                  <option value="">Select…</option>
                  {MODELS.map((m) => <option key={m.title}>{m.title}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-espresso mb-2">Requested services</label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => (
                    <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`inquiry-service-${s}`}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors ${form.services.includes(s) ? "bg-espresso text-ivory border-espresso" : "bg-ivory text-espresso border-champagne/40 hover:border-champagne"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-espresso mb-2">Project goals</label>
                <textarea required rows={4} value={form.project_goals} onChange={(e) => set("project_goals", e.target.value)} className={inputCls} data-testid="inquiry-goals" />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Estimated budget</label>
                <input value={form.budget} onChange={(e) => set("budget", e.target.value)} className={inputCls} placeholder="e.g. $2,000–$5,000" data-testid="inquiry-budget" />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso mb-2">Timeline</label>
                <input value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className={inputCls} placeholder="e.g. Within 30 days" data-testid="inquiry-timeline" />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={loading} data-testid="inquiry-submit" className="inline-flex items-center gap-2 bg-espresso text-ivory rounded-full px-8 py-4 font-medium hover:bg-forest transition-colors disabled:opacity-60">
                  {loading && <Loader2 className="animate-spin" size={18} />} Submit Inquiry
                </button>
              </div>
            </form>
          </Reveal>
        )}
      </section>
    </div>
  );
}
